/**
 * Floating Support Widget — persistent chat bubble bottom-right, visible on
 * every `/app/*` page. Users click to open a slide-up panel with the AI
 * Support Agent.
 *
 * Features: live chat, typing indicator, minimize/reopen, end conversation,
 * report-a-bug shortcut, screenshot attachment, escalation confirmation.
 */
import { useEffect, useRef, useState } from "react";
import { api } from "@/lib/api";
import { toast } from "sonner";
import {
  MessageSquare, X, Minus, Bug, Send, Paperclip,
  ShieldAlert, CheckCircle2, Loader2,
} from "lucide-react";
import { useAuth } from "@/context/AuthContext";
import { Link, useLocation } from "react-router-dom";

const OPEN_KEY = "careeros-support-open";
const CASE_KEY = "careeros-support-active-case";

export default function SupportWidget() {
  const { user } = useAuth();
  const location = useLocation();
  const [open, setOpen] = useState(() => localStorage.getItem(OPEN_KEY) === "1");
  const [minimized, setMinimized] = useState(false);
  const [caseId, setCaseId] = useState(() => localStorage.getItem(CASE_KEY) || null);
  const [messages, setMessages] = useState([]);   // {role, content, at}
  const [draft, setDraft] = useState("");
  const [isBugMode, setIsBugMode] = useState(false);
  const [busy, setBusy] = useState(false);
  const [status, setStatus] = useState("open");
  const [escalated, setEscalated] = useState(false);
  const scrollRef = useRef(null);

  // Only show for authenticated users (Support Widget requires auth)
  // — matches the API's Depends(get_current_user_id)
  const enabled = !!user;

  useEffect(() => {
    localStorage.setItem(OPEN_KEY, open ? "1" : "0");
  }, [open]);

  useEffect(() => {
    if (caseId) localStorage.setItem(CASE_KEY, caseId);
    else localStorage.removeItem(CASE_KEY);
  }, [caseId]);

  // Load existing case messages if we have a case ID
  useEffect(() => {
    if (!open || !caseId) return;
    api.get(`/support/case/${caseId}`).then((r) => {
      setMessages((r.data.messages || []).map((m) => ({
        role: m.role, content: m.content, at: m.created_at,
      })));
      setStatus(r.data.case?.status || "open");
      setEscalated(!!r.data.case?.email_sent || r.data.case?.status === "escalated");
    }).catch((e) => {
      // eslint-disable-next-line no-console
      console.debug("[Support] case load:", e);
      // Case may have been closed on server; drop it
      setCaseId(null);
      setMessages([]);
    });
  }, [caseId, open]);

  useEffect(() => {
    if (scrollRef.current) scrollRef.current.scrollTop = scrollRef.current.scrollHeight;
  }, [messages, busy]);

  const send = async (text, opts = {}) => {
    if (!text.trim() || busy) return;
    setBusy(true);
    const nowIso = new Date().toISOString();
    // Optimistic user bubble
    setMessages((m) => [...m, { role: "user", content: text.trim(), at: nowIso }]);
    setDraft("");
    try {
      const { data } = await api.post("/support/chat", {
        case_id: caseId,
        message: text.trim(),
        is_bug_report: !!opts.is_bug_report,
        page_affected: location.pathname,
        browser: navigator.userAgent,
        device: /Mobi/.test(navigator.userAgent) ? "mobile" : "desktop",
        operating_system: navigator.platform,
      });
      setCaseId(data.case_id);
      setStatus(data.status);
      setEscalated(data.escalated);
      setMessages((m) => [...m, { role: "assistant", content: data.reply, at: new Date().toISOString() }]);
      if (data.escalated) {
        toast.success(`Escalated to human support · Case ${data.case_id}`);
      }
    } catch (e) {
      const detail = e.response?.data?.detail || "Something went wrong. Please try again.";
      setMessages((m) => [...m, { role: "assistant", content: `⚠ ${detail}`, at: new Date().toISOString() }]);
    } finally {
      setBusy(false);
      setIsBugMode(false);
    }
  };

  const endConversation = async () => {
    if (!caseId) { setOpen(false); return; }
    try { await api.post(`/support/case/${caseId}/end`); }
    catch (e) { /* eslint-disable-next-line no-console */ console.debug("[Support] end:", e); }
    toast.success("Conversation ended · saved to your case history");
    setCaseId(null);
    setMessages([]);
    setStatus("closed");
    setEscalated(false);
    setOpen(false);
  };

  const startBugReport = () => {
    setIsBugMode(true);
    setDraft("I'd like to report a bug: ");
    setTimeout(() => document.getElementById("support-input")?.focus(), 50);
  };

  const attachScreenshot = async (file) => {
    if (!caseId) return toast.error("Send a message first, then attach.");
    if (file.size > 3 * 1024 * 1024) return toast.error("Max 3MB per attachment");
    const reader = new FileReader();
    reader.onload = async () => {
      const dataUrl = reader.result;
      const base64 = dataUrl.split(",")[1];
      try {
        await api.post(`/support/case/${caseId}/attach`, {
          filename: file.name, mime: file.type, data_base64: base64,
        });
        toast.success("Screenshot attached");
      } catch (e) {
        toast.error(e.response?.data?.detail || "Attachment failed");
      }
    };
    reader.readAsDataURL(file);
  };

  if (!enabled) return null;

  // Hide on certain paths (admin, login, signup) where a widget would be intrusive.
  if (location.pathname.startsWith("/admin") ||
      location.pathname.startsWith("/app/admin") ||
      location.pathname === "/login" || location.pathname === "/signup" ||
      location.pathname.startsWith("/app/interview/live")) {
    return null;
  }

  if (!open) {
    return (
      <button
        data-testid="support-widget-button"
        onClick={() => { setOpen(true); setMinimized(false); }}
        className="fixed bottom-5 right-5 z-40 h-14 w-14 rounded-full shadow-lg bg-primary text-primary-foreground hover:scale-105 active:scale-95 transition-transform flex items-center justify-center"
        title="Support"
      >
        <MessageSquare size={22} />
      </button>
    );
  }

  return (
    <div
      data-testid="support-widget-panel"
      className={`fixed bottom-5 right-5 z-40 bg-card border border-border rounded-2xl shadow-2xl flex flex-col overflow-hidden
        ${minimized ? "w-72 h-14" : "w-[380px] h-[560px] max-h-[calc(100vh-80px)]"}
        transition-all duration-200`}
    >
      {/* Header */}
      <div className="flex items-center gap-2 px-3 py-2.5 border-b border-border bg-muted/40">
        <div className="h-2 w-2 rounded-full bg-emerald-500 animate-pulse" />
        <div className="text-sm font-semibold flex-1">CareerOS Support</div>
        {caseId && <div className="text-[10px] font-mono text-muted-foreground" data-testid="support-case-id">{caseId}</div>}
        <button onClick={() => setMinimized(!minimized)} className="p-1 rounded hover:bg-muted" data-testid="support-minimize" title="Minimize">
          <Minus size={14} />
        </button>
        <button onClick={endConversation} className="p-1 rounded hover:bg-muted" data-testid="support-close" title="End conversation">
          <X size={14} />
        </button>
      </div>

      {!minimized && (
        <>
          {/* Escalation banner */}
          {escalated && (
            <div className="px-3 py-2 bg-[hsl(var(--warning))/0.12] border-b border-[hsl(var(--warning))/0.3] text-[11px] text-[hsl(var(--warning))] flex items-center gap-2" data-testid="support-escalated-banner">
              <ShieldAlert size={12} /> Escalated to CareerOS support team · they'll review by priority.
            </div>
          )}
          {status === "resolved" && (
            <div className="px-3 py-2 bg-[hsl(var(--success))/0.12] border-b border-[hsl(var(--success))/0.3] text-[11px] text-[hsl(var(--success))] flex items-center gap-2">
              <CheckCircle2 size={12} /> Case marked resolved. Reply to reopen or click X to close.
            </div>
          )}

          {/* Messages */}
          <div ref={scrollRef} className="flex-1 overflow-y-auto px-3 py-3 space-y-2 text-sm" data-testid="support-messages">
            {messages.length === 0 && (
              <div className="text-center text-muted-foreground py-8">
                <MessageSquare size={24} className="mx-auto mb-2 opacity-40" />
                <div className="text-sm">Hi! I'm the CareerOS Support Agent.</div>
                <div className="text-xs mt-1">Ask about features, billing, bugs, or anything else.</div>
                <div className="mt-4">
                  <button onClick={startBugReport} className="inline-flex items-center gap-1.5 rounded-full border border-border px-3 py-1.5 text-xs hover:bg-muted" data-testid="support-bug-shortcut">
                    <Bug size={12} /> Report a bug
                  </button>
                </div>
              </div>
            )}
            {messages.map((m, i) => (
              <MessageBubble key={`${m.at}-${i}`} m={m} />
            ))}
            {busy && (
              <div className="flex items-center gap-2 text-xs text-muted-foreground pl-2" data-testid="support-typing">
                <div className="flex gap-1">
                  <span className="h-1.5 w-1.5 rounded-full bg-primary animate-bounce" style={{ animationDelay: "0ms" }} />
                  <span className="h-1.5 w-1.5 rounded-full bg-primary animate-bounce" style={{ animationDelay: "150ms" }} />
                  <span className="h-1.5 w-1.5 rounded-full bg-primary animate-bounce" style={{ animationDelay: "300ms" }} />
                </div>
                Support agent is typing…
              </div>
            )}
          </div>

          {/* Input */}
          <div className="border-t border-border p-2">
            {isBugMode && (
              <div className="text-[10px] font-mono uppercase tracking-widest text-[hsl(var(--warning))] mb-1 flex items-center gap-1">
                <Bug size={10} /> Bug report mode — describe what went wrong
              </div>
            )}
            <div className="flex items-end gap-1.5 bg-background border border-border rounded-xl px-2 py-1.5">
              <textarea
                id="support-input"
                data-testid="support-input"
                rows={1}
                value={draft}
                onChange={(e) => setDraft(e.target.value)}
                onKeyDown={(e) => {
                  if (e.key === "Enter" && !e.shiftKey) {
                    e.preventDefault();
                    send(draft, { is_bug_report: isBugMode });
                  }
                }}
                placeholder="Type your message…"
                disabled={busy || status === "closed"}
                className="flex-1 resize-none bg-transparent outline-none text-sm py-1.5 max-h-24"
              />
              <label className="p-1.5 rounded hover:bg-muted cursor-pointer" title="Attach screenshot" data-testid="support-attach">
                <input type="file" accept="image/*" hidden onChange={(e) => e.target.files?.[0] && attachScreenshot(e.target.files[0])} />
                <Paperclip size={14} />
              </label>
              <button
                data-testid="support-send"
                onClick={() => send(draft, { is_bug_report: isBugMode })}
                disabled={!draft.trim() || busy}
                className="p-1.5 rounded bg-primary text-primary-foreground disabled:opacity-30 hover:opacity-90"
              >
                {busy ? <Loader2 size={14} className="animate-spin" /> : <Send size={14} />}
              </button>
            </div>
            <div className="flex items-center justify-between px-1 mt-1.5 text-[10px] text-muted-foreground">
              <Link to="/app/support" className="hover:underline">See all cases</Link>
              <span>Powered by CareerOS AI</span>
            </div>
          </div>
        </>
      )}
    </div>
  );
}

function MessageBubble({ m }) {
  const isUser = m.role === "user";
  const time = m.at ? new Date(m.at).toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" }) : "";
  return (
    <div className={`flex ${isUser ? "justify-end" : "justify-start"}`}>
      <div className={`max-w-[85%] rounded-xl px-3 py-2 text-sm whitespace-pre-wrap
        ${isUser ? "bg-primary/15 text-foreground" : "bg-muted/60 text-foreground"}`}>
        {m.content}
        {time && <div className="text-[9px] text-muted-foreground mt-1 opacity-70">{time}</div>}
      </div>
    </div>
  );
}
